import type { SlideDeck } from './slideData';
import { BAND1_MONTHLY, BAND1_ANNUAL, ONBOARDING_BASE, MIN_USERS } from '../lib/pricingConstants';

const customCSS = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');

.dark-theme .reveal { font-family: 'DM Sans', sans-serif; }
.dark-theme .reveal h1, .dark-theme .reveal h2 {
	font-family: 'DM Sans', sans-serif;
	color: #e8e8f0;
	text-transform: none;
	letter-spacing: -0.02em;
	font-weight: 700;
}
.dark-theme .reveal h1 { font-size: 2.6em; line-height: 1.1; }
.dark-theme .reveal h2 { font-size: 1.8em; line-height: 1.2; margin-bottom: 0.6em; }
.dark-theme .reveal h3 { font-size: 1.2em; line-height: 1.3; color: #E8491C; font-weight: 600; font-family: 'DM Sans', sans-serif; }
.dark-theme .reveal p, .dark-theme .reveal li { font-size: 0.72em; line-height: 1.6; color: #f0f0f5; }
.dark-theme .reveal ul { list-style: none; padding-left: 0; }
.dark-theme .reveal ul li { padding: 0.3em 0 0.3em 1.4em; position: relative; }
.dark-theme .reveal ul li::before { content: ''; position: absolute; left: 0; top: 0.75em; width: 6px; height: 6px; border-radius: 50%; background: #E8491C; }
.dark-theme .reveal .slides section { padding: 40px 60px; text-align: left; }

.lp-accent { width: 60px; height: 4px; background: #E8491C; border-radius: 2px; margin-bottom: 1.2em; }

.lp-bq { background: rgba(255,255,255,0.04); border-left: 4px solid #E8491C; padding: 0.8em 1.2em; margin: 0.8em 0; border-radius: 0 8px 8px 0; width: 90%; box-shadow: none; }
.lp-bq p { color: #f0f0f5 !important; font-size: 0.78em !important; font-weight: 400; }

.lp-section { text-align: center !important; }
.lp-section-num { font-family: 'JetBrains Mono', monospace; font-size: 0.6em; color: #E8491C; font-weight: 500; margin-bottom: 0.4em; display: block; }

.lp-icon { display: inline-flex; align-items: center; justify-content: center; width: 44px; height: 44px; border-radius: 10px; font-size: 1.2em; margin-bottom: 12px; }
.lp-icon-orange { background: rgba(232,73,28,0.15); }
.lp-icon-green { background: rgba(34,197,94,0.15); }
.lp-icon-blue { background: rgba(59,130,246,0.15); }
.lp-icon-amber { background: rgba(245,158,11,0.15); }
.lp-icon-purple { background: rgba(139,92,246,0.15); }

.lp-card-row { display: flex; gap: 20px; margin-top: 0.8em; }
.lp-card { flex: 1; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.06); border-radius: 12px; padding: 24px; }
.lp-card h3 { margin-top: 0; margin-bottom: 0.3em; font-size: 1em; }
.lp-card .card-q { font-size: 0.65em; color: #9898b0; font-style: italic; margin-bottom: 0.6em; }
.lp-card p, .lp-card li { font-size: 0.62em; }

.lp-pricing-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 0.8em; }
.lp-price-card { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.06); border-radius: 12px; padding: 24px; text-align: center; }
.lp-price-card.highlight { border-color: #E8491C; background: rgba(232,73,28,0.06); }
.lp-price-card .label { font-size: 0.6em; color: #9898b0; text-transform: uppercase; letter-spacing: 0.08em; font-weight: 600; }
.lp-price-card .amount { font-size: 1.8em; font-weight: 700; color: #e8e8f0; margin: 0.2em 0; }
.lp-price-card .amount .currency { font-size: 0.6em; vertical-align: top; }
.lp-price-card .detail { font-size: 0.55em; color: #9898b0; }

.lp-big-stat { font-size: 1.875em; font-weight: 700; color: #E8491C; line-height: 1; margin: 0.2em 0; }

.lp-flow { display: flex; align-items: center; gap: 0; margin-top: 1em; justify-content: center; }
.lp-flow-step { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 10px; padding: 16px 20px; text-align: center; min-width: 120px; }
.lp-flow-step .name { font-size: 0.7em; font-weight: 600; color: #e8e8f0; }
.lp-flow-step .desc { font-size: 0.52em; color: #9898b0; margin-top: 4px; }
.lp-flow-arrow { font-size: 1.2em; color: #E8491C; padding: 0 8px; }
`;

// Derived pricing for slides (5-10 users, monthly)
const saasLow = (5 * BAND1_MONTHLY * 12).toLocaleString();
const saasHigh = (10 * BAND1_MONTHLY * 12).toLocaleString();
const yearOneLow = (5 * BAND1_MONTHLY * 12 + ONBOARDING_BASE).toLocaleString();
const yearOneHigh = (10 * BAND1_MONTHLY * 12 + ONBOARDING_BASE).toLocaleString();
const onboardingDisplay = ONBOARDING_BASE.toLocaleString();

const minMonthly = (MIN_USERS * BAND1_MONTHLY).toLocaleString();

export const productPresentation: SlideDeck = {
	slug: 'livewire-product',
	name: 'Livewire Product Overview',
	subtitle: 'Cloud-based spatial digital twin for electric utilities',
	color: '#1a1a2e',
	darkTheme: true,
	customCSS,
	slides: [
		// 1 — Title
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<h1 style="font-size:3em;margin-bottom:0.1em;font-family:lvwr-ubuntu-thin,sans-serif;">
					<span style="color:#E8491C;font-weight:700;">Livewire</span>
				</h1>
				<p style="font-size:1.1em;color:#f0f0f5;font-weight:400;margin-top:0.3em;margin-bottom:0.5em;">
					Cloud-Based Spatial Digital Twin
				</p>
				<p style="font-size:0.8em;color:#9898b0;margin-bottom:1.5em;">
					See, track, and analyze electric distribution networks from anywhere.
				</p>
				<div class="fragment" style="font-size:0.6em;color:#f0f0f5;border-top:1px solid rgba(255,255,255,0.08);padding-top:1em;margin-top:1em;">
					<br>Rhys Stewart <br> Auchindown<br>
				</div>`,
		},
		// 2 — What is Livewire
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="lp-accent"></div>
				<h2>What is Livewire?</h2>
				<ul>
					<li class="fragment">A <strong style="color:#e8e8f0;">spatial digital twin</strong> of your electric distribution network — every asset, every connection, every phase</li>
					<li class="fragment">Browser-based — no GIS software, no desktop installation, no IT infrastructure</li>
					<li class="fragment">Purpose-built for electric utilities — not a general GIS with utility extensions bolted on</li>
					<li class="fragment">Model, query, analyze, and document your network from anywhere</li>
				</ul>
				<div class="lp-bq fragment" style="margin-top:1em;">
					<p>One platform. Every question about your network answered in seconds, not days.</p>
				</div>`,
		},
		// 3 — Section 01
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="lp-section">
					<span class="lp-section-num">01</span>
					<h2 style="font-size:2.2em;">Capabilities</h2>
					<p style="max-width:600px;margin:0 auto;">Seven tools for understanding your network</p>
				</div>`,
		},
		// 4 — Spotlight
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="lp-icon lp-icon-orange">🔍</div>
				<h3>Spotlight</h3>
				<p style="font-size:0.78em;margin-bottom:0.6em;">
					Draw a boundary around any area and instantly see what's inside.
				</p>
				<ul>
					<li class="fragment">Asset counts, types, conditions, and connectivity — in seconds</li>
					<li class="fragment">Zoom into a feeder zone, a parish, a neighborhood — any boundary you define</li>
					<li class="fragment">Answer ad-hoc questions without waiting for someone to pull a report</li>
				</ul>`,
		},
		// 5 — Tracing
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="lp-icon lp-icon-purple">⚡</div>
				<h3>Tracing</h3>
				<p style="font-size:0.78em;margin-bottom:0.6em;">
					Select any point on the network and trace upstream to the source or downstream to every connected customer — by phase.
				</p>
				<ul>
					<li class="fragment">How many customers are downstream of a specific switch or conductor?</li>
					<li class="fragment">Is this feeder balanced across phases — or is load concentrated on one?</li>
					<li class="fragment">What's the full path from substation to a specific transformer?</li>
				</ul>`,
		},
		// 6 — Chronicle
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="lp-icon lp-icon-blue">📋</div>
				<h3>Chronicle</h3>
				<p style="font-size:0.78em;margin-bottom:0.6em;">
					Track every change to the network over time. Compare any two points in history.
				</p>
				<ul>
					<li class="fragment">See what was added, removed, or modified — and when</li>
					<li class="fragment">Verify infrastructure upgrades and capital project delivery</li>
					<li class="fragment">Pre-storm baseline vs. post-storm reality — spatial evidence, not reports</li>
				</ul>`,
		},
		// 7 — Annotations
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="lp-icon lp-icon-green">📌</div>
				<h3>Annotations</h3>
				<p style="font-size:0.78em;margin-bottom:0.6em;">
					Document field findings directly in Livewire — tied to the specific asset, geolocated and timestamped.
				</p>
				<ul>
					<li class="fragment">Photos, comments, and observations from phones or tablets</li>
					<li class="fragment">Flag discrepancies — missing equipment, mismatched records, unreported conditions</li>
					<li class="fragment">Build a living record that survives staff turnover</li>
				</ul>`,
		},
		// 8 — Reliability Metrics
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="lp-icon" style="background:rgba(168,85,247,0.15);">📊</div>
				<h3>Reliability Metrics</h3>
				<p style="font-size:0.78em;margin-bottom:0.6em;">
					Calculate SAIDI, SAIFI, and CAIDI directly from network topology.
				</p>
				<ul>
					<li class="fragment">Derive reliability indices from the model — not from manually assembled spreadsheets</li>
					<li class="fragment">Break down performance by feeder, zone, or any spatial boundary</li>
					<li class="fragment">Compare current vs. hypothetical configurations — quantify the impact of every change</li>
				</ul>`,
		},
		// 9 — Network Snapshots
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="lp-icon lp-icon-amber">📸</div>
				<h3>Network Snapshots</h3>
				<p style="font-size:0.78em;margin-bottom:0.6em;">
					Capture point-in-time snapshots. Compare any two moments in your network's history.
				</p>
				<ul>
					<li class="fragment">Before and after capital projects — prove what you built</li>
					<li class="fragment">Calculate reliability metrics on past configurations — show improvement over time</li>
					<li class="fragment">Audit-ready documentation — timestamped, immutable, verifiable</li>
				</ul>`,
		},
		// 10 — What-if Scenarios
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="lp-icon lp-icon-orange">🔄</div>
				<h3>What-if Scenarios</h3>
				<p style="font-size:0.78em;margin-bottom:0.6em;">
					Duplicate your network. Make changes. See the impact — without touching production data.
				</p>
				<ul>
					<li class="fragment">What happens if we reconductor this feeder? Add a tie switch? Install a recloser?</li>
					<li class="fragment">Compare scenarios side by side with reliability metrics for each</li>
					<li class="fragment">Justify investments with data, not intuition</li>
				</ul>`,
		},
		// 11 — Section 02
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="lp-section">
					<span class="lp-section-num">02</span>
					<h2 style="font-size:2.2em;">Platform</h2>
					<p style="max-width:600px;margin:0 auto;">How it works</p>
				</div>`,
		},
		// 12 — Platform details
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="lp-accent"></div>
				<h2>Built for how utilities actually work</h2>
				<div class="lp-card-row">
					<div class="lp-card">
						<h3>☁️ Cloud-native</h3>
						<p>No servers to maintain, no software to install. Access from any browser — office, field, or home. Always the latest version.</p>
					</div>
					<div class="lp-card">
						<h3>📱 Mobile ready</h3>
						<p>Inspect assets, annotate findings, and trace connectivity from a phone or tablet. Purpose-built for field work.</p>
					</div>
					<div class="lp-card">
						<h3>🔒 Secure</h3>
						<p>Your data is isolated, encrypted, and backed up. Role-based access control. Your network model belongs to you.</p>
					</div>
				</div>`,
		},
		// 13 — The loop
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="lp-accent"></div>
				<h2>The Livewire loop</h2>
				<div class="lp-flow" style="justify-content:center;gap:4px;">
					<div class="lp-flow-step" style="min-width:0;padding:12px 10px;">
						<div class="name">Spotlight</div>
						<div class="desc">What's<br>there?</div>
					</div>
					<div class="lp-flow-arrow">→</div>
					<div class="lp-flow-step" style="min-width:0;padding:12px 10px;">
						<div class="name">Tracing</div>
						<div class="desc">What's<br>connected?</div>
					</div>
					<div class="lp-flow-arrow">→</div>
					<div class="lp-flow-step" style="min-width:0;padding:12px 10px;">
						<div class="name">Chronicle</div>
						<div class="desc">What<br>changed?</div>
					</div>
					<div class="lp-flow-arrow">→</div>
					<div class="lp-flow-step" style="min-width:0;padding:12px 10px;">
						<div class="name">Annotations</div>
						<div class="desc">What's on<br>the ground?</div>
					</div>
					<div class="lp-flow-arrow">→</div>
					<div class="lp-flow-step" style="min-width:0;padding:12px 10px;">
						<div class="name">Reliability</div>
						<div class="desc">How's it<br>performing?</div>
					</div>
					<div class="lp-flow-arrow">→</div>
					<div class="lp-flow-step" style="min-width:0;padding:12px 10px;border-color:#E8491C;">
						<div class="name" style="color:#E8491C;">Snapshots</div>
						<div class="desc">Capture<br>the truth</div>
					</div>
				</div>
				<p style="text-align:center;margin-top:1.2em;font-size:0.72em;">
					See it. Trace it. Track it. Document it. Measure it. Capture it.
				</p>`,
		},
		// 14 — Demo
		{
			title: '',
			background: '#0f0f1e',
			content: `
				<div class="lp-section">
					<span class="lp-section-num">03</span>
					<div style="display:inline-block;background:#E8491C;color:white;font-size:0.5em;font-weight:600;padding:4px 14px;border-radius:20px;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:0.6em;">Live Demo</div>
					<h2 style="font-size:2.2em;">Everglades Electric</h2>
					<p style="max-width:600px;margin:0 auto;">See Livewire in action</p>
				</div>`,
		},
		// Closing
		{
			title: '',
			background: '#1a1a2e',
			content: `
				<div class="lp-section">
					<h1 style="margin-bottom:0.2em;font-size:3em;font-family:lvwr-ubuntu-thin,sans-serif;">
						<span style="color:#E8491C;">Livewire</span>
					</h1>
					<p style="font-size:0.85em;color:#9898b0;margin-bottom:1.5em;">Your network. Modeled, tracked, and understood.</p>
					<p style="font-size:0.7em;color:#e8e8f0;">
						Rhys Stewart<br>
						<span style="color:#9898b0;">rhys@auchindown.com · lvwr.io</span>
					</p>
				</div>`,
			verticalSlides: [
				{
					title: '',
					background: '#1a1a2e',
					content: `
						<div class="lp-accent"></div>
						<h2>Simple, accessible pricing</h2>
						<div class="lp-card" style="text-align:center;border-color:rgba(232,73,28,0.3);background:rgba(232,73,28,0.06);">
							<p class="card-q">Starting price</p>
							<div class="lp-big-stat" style="font-size:3.2em;">$${minMonthly}<span style="font-size:0.28em;color:#9898b0;font-weight:600;"> / month</span></div>
							<p>Minimum ${MIN_USERS} users at $${BAND1_MONTHLY}/user, then scale up</p>
						</div>
						<div class="lp-card-row" style="margin-top:16px;">
							<div class="lp-card" style="text-align:center;">
								<p class="card-q">Monthly</p>
								<div class="lp-big-stat">$${BAND1_MONTHLY}</div>
								<p>per user / month</p>
							</div>
							<div class="lp-card" style="text-align:center;">
								<p class="card-q">Annual commitment</p>
								<div class="lp-big-stat">$${BAND1_ANNUAL}</div>
								<p>per user / month · 10+ users</p>
							</div>
						</div>
						<p style="margin-top:0.9em;font-size:0.62em;text-align:center;">
							100 GB storage included · No GIS licensing required · Browser-based
						</p>`,
				},
				{
					title: '',
					background: '#1a1a2e',
					content: `
						<div class="lp-accent"></div>
						<h2>For a 5 to 10 person team</h2>
						<div class="lp-card-row">
							<div class="lp-card" style="text-align:center;">
								<p class="card-q">SaaS</p>
								<div class="lp-big-stat">$${saasLow} – $${saasHigh}</div>
								<p>per year</p>
							</div>
							<div class="lp-card" style="text-align:center;">
								<p class="card-q">Onboarding (one-time)</p>
								<div class="lp-big-stat">$${onboardingDisplay}+</div>
								<p>Jumpstart — 2 months to go-live</p>
							</div>
						</div>
						<div class="lp-card" style="margin-top:16px;text-align:center;border-color:rgba(232,73,28,0.2);">
							<p class="card-q">Year 1 all-in</p>
							<div class="lp-big-stat">$${yearOneLow} – $${yearOneHigh}</div>
							<p>Cloud-based power delivery modeling without the enterprise price tag</p>
						</div>`,
				},
			],
		},
	],
};
